"use client";

import { useState } from "react";
import { Check, Loader2, Rocket, Zap, Building2, Users, X, ExternalLink, CheckCircle } from "lucide-react";
import { subscriptionsApi } from "@/lib/api";
import { detectCurrency, convertPrice, CURRENCY_MAP } from "@/lib/currency";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useRouter } from "next/navigation";
import ContactSalesModal from "../ContactSalesModal";
import { Button } from "../ui/button";

const PLAN_BASE_PRICES: Record<string, number> = {
    'starter': 15,
    'professional': 39,
    'business': 119,
    'enterprise': 0
};

const plans = [
    {
        id: "starter",
        name: "Starter",
        icon: Rocket,
        priceGBP: 15,
        description: "For individual recruiters and small teams. Includes 1-week free trial.",
        features: [
            "10 Interview analyses / month",
            "100 CV Screenings / month",
            "1 Admin user only",
            "Audio upload & transcription",
            "HR summary generation",
            "Secure cloud storage"
        ],
        cta: "Start Free Trial",
        popular: false,
        color: "text-blue-600",
        bgIcon: "bg-blue-100 dark:bg-blue-900/40"
    },
    {
        id: "professional",
        name: "Professional",
        icon: Zap,
        priceGBP: 39,
        description: "For growing HR teams needing advanced features.",
        features: [
            "50 Interview analyses / month",
            "500 CV Screenings / month",
            "Up to 5 team members",
            "Speaker identification",
            "Priority processing"
        ],
        cta: "Get Started",
        popular: true,
        badge: "Most Popular",
        color: "text-emerald-600",
        bgIcon: "bg-emerald-100 dark:bg-emerald-900/40"
    },
    {
        id: "business",
        name: "Business",
        icon: Building2,
        priceGBP: 119,
        description: "For companies hiring at scale.",
        features: [
            "200 Interview analyses / month",
            "2000 CV Screenings / month",
            "Up to 20 team members",
            "Advanced HR summaries",
            "Priority support"
        ],
        cta: "Get Started",
        popular: false,
        color: "text-purple-600",
        bgIcon: "bg-purple-100 dark:bg-purple-900/40"
    },
    {
        id: "enterprise",
        name: "Enterprise",
        icon: Users,
        priceGBP: null, // Custom pricing
        description: "Negotiable pricing based on your high-volume needs.",
        features: [
            "1000+ Interview analyses / mo",
            "10,000+ CV Screenings / mo",
            "200+ team members",
            "Custom AI workflows",
            "SLA & dedicated support"
        ],
        cta: "Contact Sales",
        popular: false,
        color: "text-amber-600",
        bgIcon: "bg-amber-100 dark:bg-amber-900/40"
    }
];

export default function Pricing() {
    const { user } = useAuth();
    const router = useRouter();
    const [isYearly, setIsYearly] = useState(false);
    const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
    const [currency, setCurrency] = useState(CURRENCY_MAP.USD);
    const [showContactModal, setShowContactModal] = useState(false);
    const [pendingUpgrade, setPendingUpgrade] = useState<{ planId: string; isYearly: boolean } | null>(null);
    const [previewData, setPreviewData] = useState<{ 
        amount_due: number; 
        next_renewal_amount?: number;
        subtotal?: number;
        total?: number;
        starting_balance?: number;
        currency: string; 
        next_payment_date: string; 
        is_new: boolean; 
        is_upgrade?: boolean;
        line_items?: Array<{
            amount: number;
            description: string;
            period_start: string;
            period_end: string;
            proration?: boolean;
        }>
    } | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [subscription, setSubscription] = useState<any>(null);

    useEffect(() => {
        setCurrency(detectCurrency());
        if (user) {
            fetchSubscription();
        }
    }, [user]);

    const fetchSubscription = async () => {
        try {
            const data = await subscriptionsApi.getSubscription();
            setSubscription(data);
        } catch (error) {
            console.error("Error fetching subscription:", error);
        }
    };

    const canUpgradeTo = (targetPlan: string, targetCycle: string) => {
        if (!subscription) return true;
        if (subscription.is_trial_active) return true;
        if (targetPlan === 'enterprise') return true;
        
        const planLevels: Record<string, number> = {
          'starter': 1,
          'professional': 2,
          'business': 3,
          'enterprise': 4
        };
        
        const currentLevel = planLevels[subscription.plan] || 1;
        const targetLevel = planLevels[targetPlan] || 1;
        
        const currentCycle = subscription.billing_cycle || 'monthly';
        
        // Rule 1: No tier downgrades
        if (targetLevel < currentLevel) return false;
        
        // Rule 2: If currently yearly, cannot switch to monthly
        if (currentCycle === 'yearly' && targetCycle === 'monthly') return false;
        
        // If same plan and same cycle, it's the current plan
        if (targetLevel === currentLevel && targetCycle === currentCycle) return false;
        
        return true;
    };

    const handleSubscribe = async (planId: string) => {
        if (planId === 'enterprise') {
            setShowContactModal(true);
            return;
        }

        if (!user) {
            router.push("/signup");
            return;
        }

        const targetCycle = isYearly ? 'yearly' : 'monthly';
        if (!subscription?.is_trial_active && !canUpgradeTo(planId, targetCycle)) {
            return;
        }

        setPendingUpgrade({ planId, isYearly });

        // Fetch preview from backend if they are logged in and might be upgrading
        setPreviewLoading(true);
        try {
            const fullPlanId = `${planId}_${targetCycle}`;
            const data = await subscriptionsApi.previewUpgrade(fullPlanId);
            setPreviewData(data);
        } catch (error) {
            console.error("Error fetching preview:", error);
            setPreviewData(null);
        } finally {
            setPreviewLoading(false);
        }
    };

    const confirmUpgrade = async () => {
        if (!pendingUpgrade) return;
        const { planId, isYearly } = pendingUpgrade;

        setLoadingPlan(planId);
        try {
            const fullPlanId = `${planId}_${isYearly ? 'yearly' : 'monthly'}`;
            const { checkout_url } = await subscriptionsApi.createCheckout(fullPlanId);
            window.location.href = checkout_url;
        } catch (error: any) {
            console.error("Checkout failed:", error);
            const message = error.response?.data?.detail || "Could not initiate checkout. Your session might have expired. Please try logging in again.";
            alert(message);
        } finally {
            setLoadingPlan(null);
            setPendingUpgrade(null);
        }
    };

    return (
        <section id="pricing" className="py-24 bg-slate-50 dark:bg-slate-950">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-6">Simple, Transparent Pricing</h2>
                    <p className="text-slate-500 dark:text-slate-400 mb-10">Choose the plan that&apos;s right for your team&apos;s needs.</p>

                    <div className="flex items-center justify-center gap-4">
                        <span className={`text-sm font-semibold ${!isYearly ? 'text-blue-600' : 'text-slate-400'}`}>Monthly</span>
                        <button
                            onClick={() => setIsYearly(!isYearly)}
                            className="relative w-14 h-7 bg-slate-200 dark:bg-slate-800 rounded-full p-1 transition-colors duration-200"
                        >
                            <div className={`w-5 h-5 bg-blue-600 rounded-full transition-transform duration-200 ${isYearly ? 'translate-x-7' : 'translate-x-0'}`} />
                        </button>
                        <span className={`text-sm font-semibold ${isYearly ? 'text-blue-600' : 'text-slate-400'}`}>
                            Yearly <span className="ml-1 text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 px-2 py-0.5 rounded-full">Save 20%</span>
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {plans.map((plan, index) => (
                        <div
                            key={index}
                            className={`relative flex flex-col p-8 rounded-3xl bg-white dark:bg-slate-900 border ${plan.popular ? 'border-blue-500 shadow-2xl shadow-blue-500/10 scale-105 z-10' : 'border-slate-200 dark:border-slate-800'}`}
                        >
                            {plan.badge && (
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white text-xs font-black uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg">
                                    {plan.badge}
                                </div>
                            )}

                            <div className="mb-8">
                                <div className={`w-12 h-12 ${plan.bgIcon} rounded-2xl flex items-center justify-center ${plan.color} mb-6`}>
                                    <plan.icon className="w-6 h-6" />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{plan.name}</h3>
                                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{plan.description}</p>
                            </div>

                            <div className="mb-8 items-baseline gap-1">
                                {plan.priceGBP !== null ? (
                                    <>
                                        <span className="text-4xl font-black text-slate-900 dark:text-white">
                                            {currency.symbol}{convertPrice(isYearly ? plan.priceGBP * 0.8 : plan.priceGBP, currency.rate)}
                                        </span>
                                        <span className="text-slate-400 text-sm">/month</span>
                                    </>
                                ) : (
                                    <span className="text-3xl font-black text-slate-900 dark:text-white">Custom</span>
                                )}
                            </div>

                            <div className="space-y-4 mb-10 flex-1">
                                {plan.features.map((feature, fIndex) => (
                                    <div key={fIndex} className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
                                        <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                                        {feature}
                                    </div>
                                ))}
                            </div>

                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={() => handleSubscribe(plan.id)}
                                    disabled={loadingPlan !== null || (user && !canUpgradeTo(plan.id, isYearly ? 'yearly' : 'monthly'))}
                                    className={`w-full py-4 px-6 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 ${
                                        plan.popular 
                                            ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-xl shadow-blue-500/20' 
                                            : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-900 dark:text-white'
                                    } ${(user && !canUpgradeTo(plan.id, isYearly ? 'yearly' : 'monthly')) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {loadingPlan === plan.id && <Loader2 className="w-5 h-5 animate-spin" />}
                                    {user && subscription?.plan === plan.id && subscription?.billing_cycle === (isYearly ? 'yearly' : 'monthly') 
                                        ? 'Current Plan' 
                                        : user && !canUpgradeTo(plan.id, isYearly ? 'yearly' : 'monthly')
                                            ? 'Unavailable'
                                            : plan.cta}
                                </button>
                                
                                {plan.id === 'starter' && (!user || (subscription?.plan === 'starter' && subscription?.is_trial_active)) && (
                                    <button
                                        onClick={() => handleSubscribe('starter')}
                                        disabled={loadingPlan !== null}
                                        className="w-full py-2 px-6 rounded-xl font-semibold transition-all text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-200 dark:border-blue-900/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center justify-center gap-2"
                                    >
                                        Pay Now
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {showContactModal && (
                <ContactSalesModal
                    onClose={() => setShowContactModal(false)}
                    initialSubject="Enterprise Plan Inquiry"
                    initialMessage="I would like to learn more about the Enterprise plan and how it can fit our organization's needs."
                />
            )}

            {pendingUpgrade && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                    <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
                        <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800">
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white font-serif">Confirm Upgrade</h2>
                            <button
                                onClick={() => setPendingUpgrade(null)}
                                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-slate-500" />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="mb-6">
                                <p className="text-slate-600 dark:text-slate-400 mb-4 text-left">
                                    You are about to subscribe to the <span className="font-bold text-slate-900 dark:text-white capitalize">{pendingUpgrade.planId}</span> plan.
                                </p>

                                <div className="bg-slate-50 dark:bg-slate-800 rounded-2xl p-5 space-y-4">
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-slate-500">Selected Plan</span>
                                        <span className="font-bold text-slate-900 dark:text-white capitalize">{pendingUpgrade.planId}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-slate-500">Billing Cycle</span>
                                        <span className="font-bold text-slate-900 dark:text-white">{pendingUpgrade.isYearly ? 'Yearly (Save 20%)' : 'Monthly'}</span>
                                    </div>

                                    {previewData && !previewData.is_new && previewData.line_items && (
                                        <div className="pt-4 mt-4 border-t border-slate-100 dark:border-slate-800 space-y-2 text-left">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Price Breakdown</p>
                                            {previewData.line_items.map((item, idx) => (
                                                <div key={idx} className="flex justify-between items-start gap-4 text-[11px]">
                                                    <span className="text-slate-500 flex-1 leading-tight">{item.description}</span>
                                                    <span className={`font-medium ${item.amount < 0 ? 'text-emerald-600' : 'text-slate-900 dark:text-white'}`}>
                                                        {item.amount < 0 ? '-' : ''}{CURRENCY_MAP[previewData.currency as 'USD' | 'EUR' | 'GBP']?.symbol || '$'}{Math.abs(item.amount).toFixed(2)}
                                                    </span>
                                                </div>
                                            ))}
                                            {previewData.starting_balance !== 0 && (
                                                <div className="flex justify-between items-center text-[11px]">
                                                    <span className="text-slate-500">Existing Credit/Balance</span>
                                                    <span className="font-medium text-emerald-600">
                                                        {previewData.starting_balance! < 0 ? '-' : ''}{CURRENCY_MAP[previewData.currency as 'USD' | 'EUR' | 'GBP']?.symbol || '$'}{Math.abs(previewData.starting_balance!).toFixed(2)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="pt-4 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center">
                                        <span className="font-bold text-slate-900 dark:text-white">
                                            {previewData?.is_upgrade ? "Total to Pay Today" : "Amount Due Now"}
                                        </span>
                                        <div className="text-right">
                                            {previewLoading ? (
                                                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                                            ) : previewData && !previewData.is_new ? (
                                                <div className="text-2xl font-black text-blue-600">
                                                    {CURRENCY_MAP[previewData.currency as 'USD' | 'EUR' | 'GBP']?.symbol || '$'}{previewData.amount_due.toFixed(2)}
                                                    {previewData.amount_due > 0 && (
                                                        <p className="text-[10px] font-normal text-slate-500 leading-none mt-1">Includes immediate proration</p>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-2xl font-black text-blue-600">
                                                    {currency.symbol}{convertPrice(pendingUpgrade.isYearly ? (PLAN_BASE_PRICES[pendingUpgrade.planId] || 0) * 0.8 * 12 : (PLAN_BASE_PRICES[pendingUpgrade.planId] || 0), currency.rate)}
                                                    <span className="text-sm font-normal text-slate-500">/{pendingUpgrade.isYearly ? 'yr' : 'mo'}</span>
                                                    {pendingUpgrade.isYearly && (
                                                        <p className="text-[10px] font-normal text-slate-500 leading-none mt-1">
                                                            Equivalent to {currency.symbol}{convertPrice((PLAN_BASE_PRICES[pendingUpgrade.planId] || 0) * 0.8, currency.rate)}/month
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {previewData && !previewData.is_new && (
                                        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-[11px] text-blue-700 dark:text-blue-300 leading-relaxed border border-blue-100 dark:border-blue-900/30 text-left">
                                            {previewData.amount_due > 0 
                                                ? `You'll be charged ${CURRENCY_MAP[previewData.currency as 'USD' | 'EUR' | 'GBP']?.symbol || '$'}${previewData.amount_due.toFixed(2)} immediately for the upgrade. `
                                                : `Your plan will be updated with no immediate charge. `
                                            }
                                            Your next full payment of {CURRENCY_MAP[previewData.currency as 'USD' | 'EUR' | 'GBP']?.symbol || '$'}{(previewData.next_renewal_amount || 0).toFixed(2)} will be on {previewData.next_payment_date}.
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col gap-3">
                                <Button
                                    onClick={confirmUpgrade}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 rounded-2xl font-bold text-lg shadow-xl shadow-blue-500/20"
                                    disabled={loadingPlan !== null}
                                >
                                    {loadingPlan !== null ? (
                                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                    ) : (
                                        <>
                                            Confirm & Pay
                                            <ExternalLink className="w-4 h-4 ml-2" />
                                        </>
                                    )}
                                </Button>
                                <Button
                                    variant="ghost"
                                    onClick={() => setPendingUpgrade(null)}
                                    className="w-full py-4 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                                    disabled={loadingPlan !== null}
                                >
                                    Go Back
                                </Button>
                            </div>

                            <div className="mt-6 flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-100 dark:border-amber-900/20">
                                <CheckCircle className="w-4 h-4 text-amber-600 dark:text-amber-500 mt-0.5" />
                                <p className="text-[11px] text-amber-800 dark:text-amber-400 leading-relaxed text-left">
                                    By confirming, you will be redirected to Stripe for secure checkout. Your new plan benefits will be active immediately after successful payment.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
